// In-memory + sessionStorage credential store for the staff API token.
//
// The ac-admin Worker gates every route on `Authorization: Bearer <ADMIN_API_TOKEN>`
// (plus an optional `X-Admin-Email` actor header). Per ARCHITECTURE §11 (assumption
// 5) this shared-token gate is a PLACEHOLDER for SSO (OIDC) + an admin-role check;
// it is not real authentication. We keep the token only for the browser session.

const TOKEN_KEY = "ac_admin_token";
const EMAIL_KEY = "ac_admin_email";

export interface Credentials {
  token: string;
  email: string;
}

let cached: Credentials | null = null;

function read(): Credentials | null {
  if (cached) return cached;
  try {
    const token = sessionStorage.getItem(TOKEN_KEY);
    if (!token) return null;
    cached = { token, email: sessionStorage.getItem(EMAIL_KEY) ?? "" };
    return cached;
  } catch {
    return cached;
  }
}

export function getCredentials(): Credentials | null {
  return read();
}

export function getToken(): string | null {
  return read()?.token ?? null;
}

export function setCredentials(creds: Credentials): void {
  cached = creds;
  try {
    sessionStorage.setItem(TOKEN_KEY, creds.token);
    if (creds.email) sessionStorage.setItem(EMAIL_KEY, creds.email);
    else sessionStorage.removeItem(EMAIL_KEY);
  } catch {
    /* sessionStorage may be unavailable; in-memory cache still works */
  }
}

export function clearCredentials(): void {
  cached = null;
  try {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(EMAIL_KEY);
  } catch {
    /* ignore */
  }
}

export function isAuthenticated(): boolean {
  return !!getToken();
}
